import { useEffect, useId, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import type { Locale, TimelineRoute } from '../data/schema'
import { localize, localizeWithEnglish } from '../i18n'
import { flattenTimelineScenes, nextTimelineScene, type TimelineFlatScene } from '../timeline'
import type { TimelineNarrationMode, timelineNarrationOptions } from '../timelineNarration'
import { Icon } from './Icon'

export type TimelineSpeed = 0.5 | 1 | 2
export type TimelinePlaybackScope = 'era' | 'all'

interface Props {
  locale: Locale
  route: TimelineRoute
  currentScene: TimelineFlatScene
  playing: boolean
  speed: TimelineSpeed
  playbackScope: TimelinePlaybackScope
  followMap: boolean
  narrationMode?: TimelineNarrationMode
  narrationOptions?: typeof timelineNarrationOptions
  sceneDurationMs?: number
  narrationProgress?: {
    currentTimeSeconds: number
    durationSeconds: number
  }
  durationMsBySceneId?: Readonly<Record<string, number>>
  loading: boolean
  hydrating?: boolean
  error?: string
  active?: boolean
  onSelectScene: (sceneId: string) => void
  onPlay: (scope: TimelinePlaybackScope) => void
  onPause: () => void
  onRestart: () => void
  onSpeed: (speed: TimelineSpeed) => void
  onFollowMap: (follow: boolean) => void
  onNarrationMode?: (mode: TimelineNarrationMode) => void
  onNarrationSeek?: (timeSeconds: number) => void
  onRetry: () => void
  onExit: () => void
  onActivate?: () => void
}

const secondsPerScene: Record<TimelineSpeed, number> = { 0.5: 12, 1: 6, 2: 3 }

function durationLabel(seconds: number, locale: Locale) {
  const minutes = Math.floor(seconds / 60)
  const remainder = seconds % 60
  if (locale === 'en') return minutes ? `${minutes}m ${remainder}s` : `${remainder}s`
  return minutes ? `${minutes} 分 ${remainder} 秒` : `${remainder} 秒`
}

export function TimelineControls({
  locale,
  route,
  currentScene,
  playing,
  speed,
  playbackScope,
  followMap,
  narrationMode = 'off',
  narrationOptions,
  sceneDurationMs: sceneDurationMsOverride,
  narrationProgress,
  durationMsBySceneId,
  loading,
  hydrating = false,
  error,
  active = true,
  onSelectScene,
  onPlay,
  onPause,
  onRestart,
  onSpeed,
  onFollowMap,
  onNarrationMode,
  onNarrationSeek,
  onRetry,
  onExit,
  onActivate,
}: Props) {
  const popoverId = useId()
  const closePreviewTimer = useRef<number | undefined>(undefined)
  const scenes = useMemo(() => flattenTimelineScenes(route), [route])
  const [previewEraId, setPreviewEraId] = useState<string>()
  const currentEra = route.eras.find((item) => item.id === currentScene.eraId)!
  const previewEra = active ? route.eras.find((item) => item.id === previewEraId) : undefined
  const previewScenes = previewEra
    ? scenes.filter((item) => item.eraId === previewEra.id)
    : []
  const currentIndex = Math.max(0, scenes.findIndex((item) => item.scene.id === currentScene.scene.id))
  const progress = scenes.length ? ((currentIndex + 1) / scenes.length) * 100 : 0
  const defaultSceneDurationMs = secondsPerScene[speed] * 1000
  const sceneDurationMs = Math.max(1000, Math.round(sceneDurationMsOverride ?? defaultSceneDurationMs))
  const audioScrubberActive = Boolean(
    narrationProgress
    && onNarrationSeek
    && Number.isFinite(narrationProgress.durationSeconds)
    && narrationProgress.durationSeconds > 0,
  )
  const audioCurrentTimeSeconds = audioScrubberActive
    ? Math.min(Math.max(0, narrationProgress?.currentTimeSeconds ?? 0), narrationProgress?.durationSeconds ?? 0)
    : 0
  const audioDurationSeconds = audioScrubberActive ? narrationProgress?.durationSeconds ?? 0 : 0
  const audioProgressPercent = audioDurationSeconds > 0
    ? Math.round((audioCurrentTimeSeconds / audioDurationSeconds) * 1000) / 10
    : 0
  const pendingNextScene = nextTimelineScene(route, currentScene.scene.id, playbackScope)
  const [boundaryRemainingMs, setBoundaryRemainingMs] = useState(sceneDurationMs)
  const routeDurationMs = durationMsBySceneId
    ? scenes.reduce((total, item) => total + (durationMsBySceneId[item.scene.id] ?? defaultSceneDurationMs), 0)
    : scenes.length * defaultSceneDurationMs
  const allDuration = durationLabel(Math.ceil(routeDurationMs / 1000), locale)
  const routeTitle = localize(route.title, locale)
  const controlsDisabled = !active || loading || !scenes.length
  const boundaryRunning = playing && !loading && !hydrating && active
  const playingLabel = locale === 'en'
    ? playbackScope === 'era' ? `Playing ${localize(currentEra.title, locale)}` : `Playing ${routeTitle}`
    : playbackScope === 'era' ? `正在播放${localizeWithEnglish(currentEra.title, locale)}` : `正在連續播放${routeTitle}`

  useEffect(() => {
    setBoundaryRemainingMs(sceneDurationMs)
    if (!boundaryRunning || audioScrubberActive) return
    const startedAt = performance.now()
    const timer = window.setInterval(() => {
      setBoundaryRemainingMs(Math.max(0, sceneDurationMs - (performance.now() - startedAt)))
    }, 250)
    return () => window.clearInterval(timer)
  }, [audioScrubberActive, boundaryRunning, currentScene.scene.id, sceneDurationMs])

  const boundaryVisualProgressActive = boundaryRunning && !audioScrubberActive
  const boundaryProgressRatio = boundaryVisualProgressActive
    ? Math.min(1, Math.max(0, 1 - boundaryRemainingMs / sceneDurationMs))
    : 0
  const boundaryProgressPercent = Math.round(boundaryProgressRatio * 1000) / 10
  const boundaryPhase = !boundaryRunning ? 'paused' : boundaryProgressRatio >= 0.82 ? 'soon' : 'running'
  const boundaryStyle = {
    '--timeline-scene-duration': `${sceneDurationMs}ms`,
    '--timeline-boundary-progress': `${boundaryProgressPercent}%`,
  } as CSSProperties
  const effectiveBoundaryRemainingMs = boundaryRunning && audioScrubberActive
    ? Math.max(0, ((audioDurationSeconds - audioCurrentTimeSeconds) / speed) * 1000)
    : boundaryRemainingMs
  const boundaryRemainingSeconds = Math.ceil(effectiveBoundaryRemainingMs / 1000)
  const boundaryRemainingLabel = durationLabel(boundaryRemainingSeconds, locale)
  const boundaryLabel = (() => {
    if (boundaryRunning && audioScrubberActive) {
      if (locale === 'en') {
        return pendingNextScene
          ? `Audio progress controls the next chapter: ${localize(pendingNextScene.scene.title, locale)}`
          : 'Audio progress controls the end of playback.'
      }
      return pendingNextScene
        ? `旁白進度控制下一章節：${localizeWithEnglish(pendingNextScene.scene.title, locale)}`
        : '旁白進度控制時間線結束。'
    }
    if (boundaryRunning) {
      if (locale === 'en') {
        return pendingNextScene
          ? `Switches to next chapter in ${boundaryRemainingLabel}: ${localize(pendingNextScene.scene.title, locale)}`
          : `Playback ends in ${boundaryRemainingLabel}.`
      }
      return pendingNextScene
        ? `${boundaryRemainingLabel} 後切換至下一章節：${localizeWithEnglish(pendingNextScene.scene.title, locale)}`
        : `${boundaryRemainingLabel} 後結束時間線播放。`
    }
    if (locale === 'en') {
      return pendingNextScene
        ? `Next chapter countdown starts when playback resumes: ${localize(pendingNextScene.scene.title, locale)}`
        : 'Final chapter. Playback is paused.'
    }
    return pendingNextScene
      ? `恢復播放後開始倒數至下一章節：${localizeWithEnglish(pendingNextScene.scene.title, locale)}`
      : '最後章節；播放已暫停。'
  })()
  const boundaryVisiblePrefix = audioScrubberActive
    ? locale === 'en'
      ? pendingNextScene ? 'Next chapter' : 'Ending'
      : pendingNextScene ? '下一章節' : '結束'
    : locale === 'en'
      ? pendingNextScene ? 'Next chapter in' : 'Ends in'
      : pendingNextScene ? '下一章節倒數' : '結束倒數'
  const boundaryVisibleDuration = audioScrubberActive
    ? locale === 'en' ? 'Audio timing' : '隨旁白播放'
    : boundaryRemainingLabel
  const boundaryVisibleTitle = pendingNextScene ? localizeWithEnglish(pendingNextScene.scene.title, locale) : ''
  const boundaryResetKey = `${currentScene.scene.id}-${speed}-${playbackScope}-${boundaryRunning ? 'playing' : 'idle'}`

  const chooseScene = (sceneId: string) => {
    if (!active) return
    setPreviewEraId(undefined)
    onSelectScene(sceneId)
  }

  const chooseSceneByIndex = (index: number) => {
    const next = scenes[Math.min(Math.max(index, 0), scenes.length - 1)]
    if (next) chooseScene(next.scene.id)
  }

  const handleScrubberKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (controlsDisabled || audioScrubberActive) return
    const pageStep = 5
    const nextIndex = {
      ArrowRight: currentIndex + 1,
      ArrowUp: currentIndex + 1,
      ArrowLeft: currentIndex - 1,
      ArrowDown: currentIndex - 1,
      PageUp: currentIndex + pageStep,
      PageDown: currentIndex - pageStep,
      Home: 0,
      End: scenes.length - 1,
    }[event.key]
    if (nextIndex === undefined) return
    event.preventDefault()
    chooseSceneByIndex(nextIndex)
  }

  const cancelPreviewClose = () => {
    if (closePreviewTimer.current) window.clearTimeout(closePreviewTimer.current)
    closePreviewTimer.current = undefined
  }

  const openPreview = (eraId: string) => {
    if (!active) return
    cancelPreviewClose()
    setPreviewEraId(eraId)
  }

  const requestPreviewClose = () => {
    cancelPreviewClose()
    closePreviewTimer.current = window.setTimeout(() => setPreviewEraId(undefined), 180)
  }

  useEffect(() => () => cancelPreviewClose(), [])

  return <section
    className={`timeline-controls timeline-controls-light${active ? ' is-active' : ' is-inactive'}${playing ? ' is-playing' : ''}${previewEra ? ' has-era-popover' : ''}`}
    aria-label={locale === 'en' ? `${routeTitle} player` : `${routeTitle}播放器`}
    data-testid="timeline-controls"
    onMouseEnter={cancelPreviewClose}
    onMouseLeave={requestPreviewClose}
    onKeyDown={(event) => {
      if (event.key === 'Escape') setPreviewEraId(undefined)
    }}
  >
    {previewEra && <div
      id={popoverId}
      className="timeline-era-popover"
      data-testid="timeline-era-drawer"
      onMouseEnter={cancelPreviewClose}
      onMouseLeave={requestPreviewClose}
    >
      <header>
        <div>
          <span className="eyebrow">{locale === 'en' ? `Era ${String(previewEra.order).padStart(2, '0')}` : `第 ${String(previewEra.order).padStart(2, '0')} 階段`}</span>
          <h2>{localizeWithEnglish(previewEra.title, locale)}</h2>
          <p>{localize(previewEra.summary, locale)}</p>
        </div>
        <button type="button" className="timeline-play-era" onClick={() => { setPreviewEraId(undefined); onPlay('era') }}>
          <Icon name="play" size={14} />
          {locale === 'en' ? 'Play this era' : '播放這個階段'}
        </button>
      </header>
      <div className="timeline-scene-picker" aria-label={locale === 'en' ? 'Scenes in this era' : '這個階段的章節'}>
        {previewEra.subEras.map((subEra) => {
          const subEraScenes = previewScenes.filter((item) => item.subEraId === subEra.id)
          if (!subEraScenes.length) return null
          return <section key={subEra.id}>
            <h3><span>{previewEra.order}.{subEra.order}</span>{localizeWithEnglish(subEra.title, locale)}</h3>
            <div>
              {subEraScenes.map((item) => <button
                key={item.scene.id}
                type="button"
                className={item.scene.id === currentScene.scene.id ? 'active' : ''}
                onClick={() => chooseScene(item.scene.id)}
              >
                <small>{String(item.scene.globalOrder).padStart(2, '0')}</small>
                <span>{localizeWithEnglish(item.scene.title, locale)}</span>
              </button>)}
            </div>
          </section>
        })}
      </div>
    </div>}

    {(loading || hydrating) && <p className="timeline-load-state" role="status">
      {locale === 'en' ? 'Loading the narrative route. Playback is paused.' : '正在載入時間線路線，播放已暫停。'}
    </p>}
    {error && <div className="timeline-error" role="alert">
      <span>{error}</span>
      <button type="button" onClick={onRetry}>{locale === 'en' ? 'Retry' : '重試'}</button>
    </div>}

    <div className="timeline-player-main">
      <div
        key={boundaryResetKey}
        className="timeline-next-boundary"
        data-testid="timeline-next-boundary"
        data-playing={boundaryRunning ? 'true' : 'false'}
        data-boundary-kind={pendingNextScene ? 'next' : 'end'}
        data-duration-ms={sceneDurationMs}
        data-remaining-ms={Math.ceil(effectiveBoundaryRemainingMs)}
        data-progress-percent={boundaryProgressPercent.toFixed(1)}
        data-audio-synced={audioScrubberActive ? 'true' : 'false'}
        data-progress-visible={boundaryVisualProgressActive ? 'true' : 'false'}
        data-phase={boundaryPhase}
        data-next-scene-id={pendingNextScene?.scene.id ?? ''}
        style={boundaryStyle}
        title={boundaryLabel}
        aria-label={boundaryLabel}
        role={audioScrubberActive ? 'status' : 'timer'}
        aria-live="off"
      >
        <i aria-hidden="true" />
        <em aria-hidden="true" />
        <span className="timeline-next-boundary-label" aria-hidden="true">
          <strong>{boundaryVisiblePrefix}</strong>
          <span className="timeline-next-boundary-separator"> · </span>
          <small>{boundaryVisibleDuration}</small>
          {boundaryVisibleTitle && <>
            <span className="timeline-next-boundary-separator"> · </span>
            <b>{boundaryVisibleTitle}</b>
          </>}
        </span>
        <span className="sr-only">{boundaryLabel}</span>
      </div>
      <div className="timeline-now-playing">
        <div className="timeline-player-kicker">
          <span className="eyebrow">{String(currentEra.order).padStart(2, '0')} · {localize(currentEra.title, locale)}</span>
          {playing && <span className="timeline-playing-status" role="status"><i />{playingLabel}</span>}
        </div>
        <strong>{localizeWithEnglish(currentScene.scene.title, locale)}</strong>
      </div>

      <div className="timeline-transport" aria-label={locale === 'en' ? 'Timeline transport' : '時間線播放控制'}>
        <button type="button" className="timeline-skip" onClick={() => currentIndex > 0 && chooseScene(scenes[currentIndex - 1].scene.id)} disabled={controlsDisabled || currentIndex <= 0} aria-label={locale === 'en' ? 'Previous scene' : '上一章'}>
          <Icon name="skip-back" />
        </button>
        <button type="button" className="timeline-play" onClick={playing ? onPause : () => { setPreviewEraId(undefined); onPlay('all') }} disabled={controlsDisabled} aria-label={locale === 'en' ? playing ? 'Pause' : 'Play complete timeline' : playing ? '暫停' : '播放完整時間線'}>
          <Icon name={playing ? 'pause' : 'play'} />
          <span>{locale === 'en' ? playing ? 'Pause' : 'Play' : playing ? '暫停' : '播放'}</span>
        </button>
        <button type="button" className="timeline-skip" onClick={() => currentIndex < scenes.length - 1 && chooseScene(scenes[currentIndex + 1].scene.id)} disabled={controlsDisabled || currentIndex >= scenes.length - 1} aria-label={locale === 'en' ? 'Next scene' : '下一章'}>
          <Icon name="skip-forward" />
        </button>
      </div>

      <div className="timeline-scrubber">
        <label htmlFor="timeline-scene">
          <span>{routeTitle}</span>
          <strong>{audioScrubberActive
            ? `${durationLabel(Math.floor(audioCurrentTimeSeconds), locale)} / ${durationLabel(Math.ceil(audioDurationSeconds), locale)}`
            : `${currentIndex + 1} / ${scenes.length}`}</strong>
        </label>
        <input
          id="timeline-scene"
          type="range"
          min={audioScrubberActive ? 0 : 1}
          max={audioScrubberActive ? audioDurationSeconds : Math.max(1, scenes.length)}
          step={audioScrubberActive ? 0.1 : 1}
          value={audioScrubberActive ? audioCurrentTimeSeconds : currentIndex + 1}
          onChange={(event) => {
            if (audioScrubberActive) {
              onNarrationSeek?.(Number(event.target.value))
              return
            }
            chooseScene(scenes[Number(event.target.value) - 1].scene.id)
          }}
          onKeyDown={handleScrubberKeyDown}
          disabled={controlsDisabled}
          aria-valuetext={audioScrubberActive
            ? `${durationLabel(Math.floor(audioCurrentTimeSeconds), locale)} / ${durationLabel(Math.ceil(audioDurationSeconds), locale)}: ${localize(currentScene.scene.title, locale)}`
            : `${currentIndex + 1} / ${scenes.length}: ${localize(currentScene.scene.title, locale)}`}
        />
        <div className="timeline-progress-track"><i style={{ width: `${audioScrubberActive ? audioProgressPercent : progress}%` }} /></div>
      </div>

      <div className="timeline-player-actions">
        <button type="button" className={`timeline-follow${followMap ? ' active' : ''}`} aria-label={locale === 'en' ? 'Follow map' : '跟隨地圖'} aria-pressed={followMap} onClick={() => onFollowMap(!followMap)} disabled={controlsDisabled} title={locale === 'en' ? 'Move and zoom the Atlas to follow the current scene' : '讓地圖跟隨章節自動移動與縮放'}>
          <Icon name="focus" />
          <span>{locale === 'en' ? 'Follow' : '跟隨'}</span>
        </button>
        <button type="button" className="timeline-restart" onClick={onRestart} disabled={controlsDisabled} aria-label={locale === 'en' ? 'Restart complete timeline' : '重新播放完整時間線'}>
          <Icon name="restart" />
          <span>{locale === 'en' ? 'Restart' : '重播'}</span>
        </button>
        <label className="timeline-speed">
          <span className="sr-only">{locale === 'en' ? 'Playback speed' : '播放速度'}</span>
          <select value={speed} onChange={(event) => onSpeed(Number(event.target.value) as TimelineSpeed)} disabled={controlsDisabled}>
            <option value={0.5}>0.5×</option>
            <option value={1}>1×</option>
            <option value={2}>2×</option>
          </select>
        </label>
        {narrationOptions && onNarrationMode && <label className="timeline-narration" title={locale === 'en' ? 'Narration track' : '旁白聲軌'}>
          <Icon name="audio" size={14} />
          <span className="sr-only">{locale === 'en' ? 'Narration track' : '旁白聲軌'}</span>
          <select value={narrationMode} onChange={(event) => onNarrationMode(event.target.value as TimelineNarrationMode)} disabled={controlsDisabled}>
            {narrationOptions.map((option) => <option key={option.id} value={option.id}>
              {localize(option.label, locale)}
            </option>)}
          </select>
        </label>}
        <button type="button" className="timeline-play-all" onClick={() => { setPreviewEraId(undefined); onPlay('all') }} disabled={controlsDisabled} title={locale === 'en' ? `Play full route · estimated ${allDuration}` : `播放完整路線 · 約 ${allDuration}`}>
          <Icon name="play" size={14} />
          <span>{locale === 'en' ? 'Full route' : '完整路線'}</span>
        </button>
      </div>
    </div>

    <nav className="timeline-era-rail" aria-label={locale === 'en' ? 'Timeline ages' : '時間線階段'}>
      <button
        type="button"
        className={`timeline-era-toggle${active ? ' active' : ''}`}
        aria-pressed={active}
        onClick={active ? onExit : onActivate}
        title={locale === 'en' ? active ? 'Exit Timeline' : 'Open Timeline' : active ? '離開時間線' : '開啟時間線'}
      >
        <Icon name="spark" size={13} />
        <span>{locale === 'en' ? 'Timeline' : '時間線'}</span>
      </button>
      {route.eras.map((era) => {
        const isCurrentEra = era.id === currentScene.eraId
        return <button
          key={era.id}
          type="button"
          className={isCurrentEra ? 'active' : ''}
          aria-current={isCurrentEra ? 'step' : undefined}
          aria-expanded={previewEra?.id === era.id}
          aria-controls={previewEra?.id === era.id ? popoverId : undefined}
          disabled={!active}
          onMouseEnter={() => openPreview(era.id)}
          onFocus={() => openPreview(era.id)}
          onClick={() => {
            if (previewEra?.id === era.id) {
              const first = scenes.find((item) => item.eraId === era.id)
              if (first) chooseScene(first.scene.id)
            } else setPreviewEraId(era.id)
          }}
        >
          <small>{String(era.order).padStart(2, '0')}</small>
          <span>{localize(era.title, locale)}</span>
        </button>
      })}
    </nav>
  </section>
}
